
import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  CalendarIcon,
  MapPinIcon,
  UserIcon,
  Link,
  Edit,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  CircleDollarSign,
  Ban,
  Pause,
  Route,
  RefreshCw
} from 'lucide-react';
import { formatDate } from '@/lib/utils';
import { Rental } from '@/services/types/rentalTypes';
import { toast } from 'sonner';
import { createSubscription, cancelSubscription } from '@/pages/admin/rentals/services/subscriptionApi';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { useRentalMileage } from '@/hooks/useRentalMileage';

interface RentalDetailsCardProps {
  rental: Rental;
  loading: boolean;
  onStatusChange: (status: string) => void;
  onEdit: (rental: Rental) => void;
  onClose: () => void;
  onDelete?: () => void;
  onRentalUpdate?: () => void;  // Добавляем колбэк для обновления данных об аренде
}

export function RentalDetailsCard({ 
  rental, 
  loading, 
  onStatusChange,
  onEdit,
  onClose,
  onDelete,
  onRentalUpdate
}: RentalDetailsCardProps) {
  // Добавим состояние для управления диалогом создания подписки
  const [showCreateSubscription, setShowCreateSubscription] = useState(false);
  const [showCancelSubscription, setShowCancelSubscription] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState({
    interval: 'month' as 'day' | 'week' | 'month' | 'year',
    amount: 0,
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  // Используем хук для работы с пробегом
  const { mileageData, loading: mileageLoading, updateTotalMileage } = useRentalMileage(rental.id);

  const handleStatusClick = async (status: string) => {
    onStatusChange(status);
  };

  const handleUpdateMileage = async () => {
    const result = await updateTotalMileage();
    if (result !== null && onRentalUpdate) {
      onRentalUpdate(); // Обновляем данные аренды после пересчета пробега
    }
  };

  const renderStatusButtons = () => {
    switch (rental.status) {
      case 'active':
        return (
          <>
            <Button
              variant="secondary"
              onClick={() => handleStatusClick('completed')}
              disabled={loading}
            >
              Завершить аренду
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleStatusClick('cancelled')}
              disabled={loading}
            >
              Отменить аренду
            </Button>
          </>
        );
      case 'pending':
        return (
          <>
            <Button
              variant="default"
              onClick={() => handleStatusClick('active')}
              disabled={loading}
            >
              Активировать аренду
            </Button>
            <Button
              variant="destructive"
              onClick={() => handleStatusClick('cancelled')}
              disabled={loading}
            >
              Отклонить аренду
            </Button>
          </>
        );
      case 'completed':
        return (
          <Button variant="outline" disabled>
            Аренда завершена
          </Button>
        );
      case 'cancelled':
        return (
          <Button variant="outline" disabled>
            Аренда отменена
          </Button>
        );
      default:
        return null;
    }
  };

  // Добавим функцию создания подписки
  const handleCreateSubscription = async () => {
    setCreateLoading(true);
    try {
      const { data, error } = await createSubscription({
        rental_id: rental.id,
        interval: subscriptionData.interval,
        amount: subscriptionData.amount,
      });

      if (error) {
        toast.error(`Не удалось создать подписку: ${error}`);
      } else {
        toast.success('Подписка успешно создана');
        setShowCreateSubscription(false);
        if (onRentalUpdate) onRentalUpdate();
      }
    } catch (err) {
      console.error('Ошибка при создании подписки:', err);
      toast.error('Произошла ошибка при создании подписки');
    } finally {
      setCreateLoading(false);
    }
  };

  // Добавим функцию отмены подписки
  const handleCancelSubscription = async () => {
    if (!rental.subscription_id) return;

    setCancelLoading(true);
    try {
      const { data, error } = await cancelSubscription(rental.subscription_id);

      if (error) {
        toast.error(`Не удалось отменить подписку: ${error}`);
      } else {
        toast.success('Подписка успешно отменена');
        setShowCancelSubscription(false);
        if (onRentalUpdate) onRentalUpdate();
      }
    } catch (err) {
      console.error('Ошибка при отмене подписки:', err);
      toast.error('Произошла ошибка при отмене подписки');
    } finally {
      setCancelLoading(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <CardTitle className="text-xl">
          Информация об аренде
          {rental.human_number && (
            <span className="ml-2 text-sm text-muted-foreground">
              #{rental.human_number}
            </span>
          )}
        </CardTitle>
        <CardDescription>
          ID: {rental.id.substring(0, 8)}...
        </CardDescription>
      </CardHeader>
      <CardContent className="pb-2">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">Статус:</div>
            <RentalStatusBadge status={rental.status} />
          </div>
          <div className="flex items-center">
            <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
            <div className="text-sm">
              Начало: {formatDate(rental.startDate)}
            </div>
          </div>
          <div className="flex items-center">
            <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
            <div className="text-sm">
              Окончание:{' '}
              {rental.endDate ? formatDate(rental.endDate) : 'Не указано'}
            </div>
          </div>
          <div className="flex items-center">
            <UserIcon className="mr-2 h-4 w-4 text-muted-foreground" />
            <div className="text-sm">Курьер: {rental.courierName}</div>
          </div>
          <div className="flex items-center">
            <BikeIcon className="mr-2 h-4 w-4 text-muted-foreground" />
            <div className="text-sm">Велосипед: {rental.bikeName}</div>
          </div>
          <div className="flex items-center">
            <CircleDollarSign className="mr-2 h-4 w-4 text-muted-foreground" />
            <div className="text-sm">Стоимость: {rental.cost}</div>
          </div>

          {/* Секция пробега */}
          <div className="mt-4 pt-2 border-t">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Route className="h-4 w-4 text-muted-foreground" />
                <div className="text-sm text-muted-foreground">Пробег:</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">
                  {mileageData?.totalMileage ? `${mileageData.totalMileage} км` : '0 км'}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleUpdateMileage}
                  disabled={mileageLoading}
                  title="Пересчитать пробег"
                >
                  <RefreshCw className={`h-3 w-3 ${mileageLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
            {mileageData?.lastUpdated && (
              <div className="text-xs text-muted-foreground mt-1">
                Последнее обновление: {formatDate(mileageData.lastUpdated)}
              </div>
            )}
          </div>

          {rental.documentsUrl && (
            <div className="flex items-center">
              <Link className="mr-2 h-4 w-4 text-muted-foreground" />
              <div className="text-sm">
                <a
                  href={rental.documentsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:underline"
                >
                  Документы
                </a>
              </div>
            </div>
          )}
          
          {/* Секция подписки - обновленная с учетом статуса подписки */}
          <div className="mt-4 pt-2 border-t">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">Подписка:</div>
              
              {rental.subscription_id ? (
                <div className="flex items-center gap-2">
                  {rental.hasActiveSubscription ? (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <CircleDollarSign className="h-3 w-3" />
                      <span>Активна</span>
                    </Badge>
                  ) : rental.subscriptionInfo?.status === 'paused' ? (
                    <Badge variant="warning" className="flex items-center gap-1">
                      <Pause className="h-3 w-3" />
                      <span>Приостановлена</span>
                    </Badge>
                  ) : (
                    <Badge variant="destructive" className="flex items-center gap-1">
                      <Ban className="h-3 w-3" />
                      <span>Отменена</span>
                    </Badge>
                  )}
                  
                  {/* Всегда показываем кнопку для создания новой подписки, если нет активной */}
                  {!rental.hasActiveSubscription && rental.status === 'active' && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowCreateSubscription(true)}
                    >
                      <CircleDollarSign className="mr-1 h-4 w-4" />
                      Создать новую
                    </Button>
                  )}
                  
                  {/* Если подписка активна, показываем кнопку для отмены */}
                  {rental.hasActiveSubscription && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-destructive border-destructive hover:bg-destructive/10"
                      onClick={() => setShowCancelSubscription(true)}
                    >
                      Отменить
                    </Button>
                  )}
                </div>
              ) : (
                rental.status === 'active' && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowCreateSubscription(true)}
                  >
                    <CircleDollarSign className="mr-1 h-4 w-4" />
                    Создать
                  </Button>
                )
              )}
            </div>
            
            {rental.subscription_id && (
              <div className="mt-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-xs w-full"
                  asChild
                >
                  <a href={`/admin/subscriptions?id=${rental.subscription_id}`}>
                    <CircleDollarSign className="mr-1 h-3 w-3" />
                    Перейти к подписке
                  </a>
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
      <CardFooter className="pt-2 flex flex-wrap gap-2">
        {renderStatusButtons()}
        <Button
          variant="ghost"
          onClick={() => onEdit(rental)}
          disabled={loading}
        >
          <Edit className="mr-2 h-4 w-4" />
          Редактировать
        </Button>
      </CardFooter>
      
      {/* Диалог для создания подписки */}
      <Dialog open={showCreateSubscription} onOpenChange={setShowCreateSubscription}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Создание подписки для аренды</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="interval" className="text-sm font-medium">
                Периодичность платежа
              </label>
              <Select
                value={subscriptionData.interval}
                onValueChange={(value) => setSubscriptionData({
                  ...subscriptionData,
                  interval: value as any
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Ежедневно</SelectItem>
                  <SelectItem value="week">Еженедельно</SelectItem>
                  <SelectItem value="month">Ежемесячно</SelectItem>
                  <SelectItem value="year">Ежегодно</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label htmlFor="amount" className="text-sm font-medium">
                Сумма платежа (₽)
              </label>
              <Input
                id="amount"
                type="number"
                value={subscriptionData.amount}
                onChange={(e) => setSubscriptionData({
                  ...subscriptionData,
                  amount: parseFloat(e.target.value)
                })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowCreateSubscription(false)}
              disabled={createLoading}
            >
              Отмена
            </Button>
            <Button 
              onClick={handleCreateSubscription}
              disabled={createLoading || subscriptionData.amount <= 0}
            >
              {createLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CircleDollarSign className="mr-2 h-4 w-4" />
              )}
              Создать подписку
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Диалог для отмены подписки */}
      <AlertDialog open={showCancelSubscription} onOpenChange={setShowCancelSubscription}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Отменить подписку?</AlertDialogTitle>
            <AlertDialogDescription>
              Вы уверены, что хотите отменить подписку для этой аренды? Клиенту больше не будут выставляться счета.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelLoading}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelSubscription}
              disabled={cancelLoading}
              className="bg-destructive hover:bg-destructive/90"
            >
              {cancelLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Отменить подписку
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

const RentalStatusBadge = ({ status }: { status: string }) => {
  let variantName: "default" | "destructive" | "outline" | "secondary" | "success" | "warning" | "info" = "default";
  let label = status;
  let icon = null;

  switch (status) {
    case 'active':
      variantName = "success";
      label = 'Активна';
      icon = <CheckCircle className="mr-1.5 h-4 w-4" />;
      break;
    case 'completed':
      variantName = "secondary";
      label = 'Завершена';
      break;
    case 'cancelled':
      variantName = "destructive";
      label = 'Отменена';
      icon = <XCircle className="mr-1.5 h-4 w-4" />;
      break;
    case 'pending':
      variantName = "warning";
      label = 'Ожидает подтверждения';
      icon = <AlertTriangle className="mr-1.5 h-4 w-4" />;
      break;
    default:
      break;
  }

  return (
    <Badge variant={variantName}>
      {icon}
      {label}
    </Badge>
  );
};

const BikeIcon = React.forwardRef<
  SVGSVGElement,
  React.HTMLAttributes<SVGSVGElement>
>(({ className, ...props }, ref) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    ref={ref}
    {...props}
  >
    <circle cx="4.5" cy="17.5" r="2.5" />
    <circle cx="16.5" cy="17.5" r="2.5" />
    <path d="M5 17h2c.9 0 1.6-.7 1.6-1.6V7.2c0-.9-.7-1.6-1.6-1.6H3" />
    <path d="M14 17h-5v-5.8c0-.9.7-1.6 1.6-1.6h2.8" />
    <path d="M22 17h-3c-.9 0-1.6-.7-1.6-1.6V9c0-.9-.7-1.6-1.6-1.6h-1.2" />
  </svg>
));
BikeIcon.displayName = "BikeIcon";
